import { Validator } from 'projects/core/src/lib/helpers/validator';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { CountryModel } from 'projects/core/src/lib/models/country.model';
import { ZoneModel } from 'projects/core/src/lib/models/zone.model';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-country-timezone',
  templateUrl: './country-timezone.component.html',
  styleUrls: ['./country-timezone.component.scss']
})
export class CountryTimezoneComponent implements OnInit, OnChanges {

  @Input() edit: boolean;
  @Input() required: boolean;
  @Input() form: UntypedFormGroup;
  @Input() country: string;
  @Input() timezone: string;

  @Output() countryChange = new EventEmitter<string>();
  @Output() timezoneChange = new EventEmitter<string>();

  loading = true;
  showTimezone = false;

  optionsCountries: Array<CountryModel> = [];
  optionsTimezones: Array<ZoneModel> = [];
  countryName: string;

  constructor(
    private api: ApiService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    if (this.form) {
      this.form.addControl('country', new UntypedFormControl(null, (this.required === true) ? [Validators.required] : []));
      this.form.addControl('timezone', new UntypedFormControl(null));
    }
    this.prefillCountryName();
    this.api.getModels('countries').subscribe({
      next: (result: any) => {
        this.buildCountryOptions(result.data);
        this.setCountryValue();
        this.setTimezoneValue();
      }
    }).add(() => {
      this.loading = false;
    });
  }

  ngOnChanges(): void {
    this.setCountryValue();
    this.setTimezoneValue();
  }

  onCountryChanged(): void {
    this.countryChange.emit(this.form.get('country').value?.country_code);
  }

  onTimezoneChanged(): void {
    this.timezoneChange.emit(this.form.get('timezone').value?.zone_name);
  }

  private prefillCountryName(): void {
    const countryName = (this.country !== null) ? this.transloco.translate('countries.' + this.country) : '';
    if (countryName !== 'countries.' + this.country) {
      this.countryName = countryName;
    }
  }

  private buildCountryOptions(data: any): void {
    const isEnglisch = this.transloco.getActiveLang() === 'en';
    if (Validator.isArray(data)) {
      this.optionsCountries = data.map((country: CountryModel) => {
        if (isEnglisch === false) {
          const countryName = this.transloco.translate('countries.' + country.country_code);
          if (countryName !== country.country_name) {
            country.country_name = countryName;
          }
        }
        return country;
      }).sort((a: CountryModel, b: CountryModel) => {
        const prioA = (a.prio !== null) ? a.prio : 10000;
        const prioB = (b.prio !== null) ? b.prio : 10000;
        if (prioA === prioB) {
          if (a.country_name < b.country_name) { return -1; }
          if (a.country_name > b.country_name) { return 1; }
          return 0;
        } else {
          return prioA - prioB;
        }
      });
      const pos = this.optionsCountries.findIndex((country: CountryModel) => {
        return country.prio === null;
      });
      this.optionsCountries.splice(pos, 0, new CountryModel().deserialize({
        country_code: 'XX',
        country_name: '____________________________',
        disabled: true
      }));
    }
  }

  private setCountryValue(): void {
    if (this.optionsCountries.length > 0) {
      const selectedCountry = this.optionsCountries.find((country: CountryModel) => {
        return country.country_code === this.country;
      });
      if (selectedCountry !== undefined) {
        this.countryName = selectedCountry.country_name;
        this.form.get('country').setValue(selectedCountry);
      } else {
        this.countryName = '';
        this.form.get('country').setValue(null);
      }
      this.setTimezoneDataAndBehaviour();
      this.onCountryChanged();
    }
  }

  private setTimezoneValue(): void {
    if (this.optionsTimezones.length > 0) {
      let timezoneToSet: string = this.timezone;
      if (this.optionsTimezones.length === 1) {
        timezoneToSet = this.optionsTimezones[0].zone_name;
      }
      if (this.form.get('timezone').value?.zone_name !== timezoneToSet) {
        this.form.get('timezone').setValue(this.optionsTimezones.find((timezone: ZoneModel) => {
          return timezone.zone_name === timezoneToSet;
        }));
        this.onTimezoneChanged();
      }
    } else if (this.form.get('timezone')) {
      this.form.get('timezone').setValue(null);
      this.onTimezoneChanged();
    }
  }

  private setTimezoneDataAndBehaviour(): void {
    this.optionsTimezones = [];
    if (this.form.get('country').value) {
      this.optionsTimezones = this.form.get('country').value.zones;
      if (this.optionsTimezones.length === 1) {
        this.setTimezoneValue();
      }
    }
    if (this.optionsTimezones.length > 1) {
      this.showTimezone = true;
      this.form.get('timezone').enable();
      if (this.required === true) {
        this.form.get('timezone').setValidators([Validators.required]);
      }
    } else {
      this.showTimezone = false;
      this.form.get('timezone').disable();
      this.form.get('timezone').clearValidators();
    }
    this.form.updateValueAndValidity();
  }

}
